import { type ChangeEvent, type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { addByScan, listCards, startRun, uploadPhoto } from "../../api/client";
import { type CardList, CardStatus } from "../../api/models";
import { StatusBadge, VerdictBadge } from "../../components/Badge";
import { Pager } from "../../components/Pager";
import { productUrl } from "../../lib/catalog";
import { errorMessage, formatDate } from "../../lib/format";
import { FetchPanel } from "./FetchPanel";

const PAGE_SIZE = 50;

function parseStatus(s: string | null): CardStatus | undefined {
  const r = CardStatus.safeParse(s);
  return r.success ? r.data : undefined;
}

export function CardsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = parseStatus(params.get("status"));
  const q = params.get("q") ?? "";
  const page = Math.max(1, Number(params.get("page")) || 1);

  const [list, setList] = useState<CardList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(q);
  const [scanId, setScanId] = useState("");
  const [busy, setBusy] = useState<"scan" | "upload" | "run" | null>(null);
  const [toolError, setToolError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    listCards({ status, q: q || undefined, page, pageSize: PAGE_SIZE })
      .then(setList)
      .catch((e) => setError(errorMessage(e)));
  }, [status, q, page]);

  useEffect(load, [load]);

  const update = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next);
  };

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    update({ q: search.trim() || undefined, page: undefined });
  };

  const submitScan = async (e: FormEvent) => {
    e.preventDefault();
    const id = scanId.trim();
    if (!id) return;
    setBusy("scan");
    setToolError(null);
    try {
      const card = await addByScan(id);
      setScanId("");
      navigate(`/cards/${card.id}`);
    } catch (err) {
      setToolError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const submitPhoto = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy("upload");
    setToolError(null);
    try {
      const image = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      const card = await uploadPhoto(image);
      navigate(`/cards/${card.id}`);
    } catch (err) {
      setToolError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const fileInput = useRef<HTMLInputElement>(null);
  const [fetchOpen, setFetchOpen] = useState(false);

  const runEngine = async () => {
    setBusy("run");
    setToolError(null);
    try {
      const run = await startRun({ statuses: ["APPROVED", "NO_PRODUCT", "PENDING"] });
      navigate(`/runs/${run.id}`);
    } catch (err) {
      setToolError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const reviewUrl = (id: number) => `/cards/${id}${status ? `?status=${status}` : ""}`;

  return (
    <>
      <div className="toolbar">
        <h1>Cards</h1>
        <label className="field">
          Status
          <select
            value={status ?? ""}
            onChange={(e) => update({ status: e.target.value || undefined, page: undefined })}
          >
            <option value="">All</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="NO_PRODUCT">Not in catalog</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </label>
        <form onSubmit={submitSearch}>
          <input
            type="search"
            placeholder="scan id, product id or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size={32}
          />
        </form>
        <span className="grow" />
        <form onSubmit={submitScan}>
          <input
            type="text"
            placeholder="Add by scan id"
            value={scanId}
            onChange={(e) => setScanId(e.target.value)}
            disabled={busy === "scan"}
            size={14}
          />
          <button type="submit" disabled={busy === "scan" || !scanId.trim()}>
            Add
          </button>
        </form>
        <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" onChange={submitPhoto} hidden />
        <button type="button" onClick={() => fileInput.current?.click()} disabled={busy === "upload"}>
          {busy === "upload" ? "Uploading…" : "Upload photo"}
        </button>
        <button type="button" onClick={() => setFetchOpen(!fetchOpen)}>
          Fetch scans
        </button>
        <button type="button" className="primary" onClick={runEngine} disabled={busy === "run"}>
          Run engine
        </button>
      </div>
      <FetchPanel open={fetchOpen} onDone={load} />
      {toolError && <p className="error">{toolError}</p>}
      {error && <p className="error">{error}</p>}
      {!list && !error && <p className="muted">Loading…</p>}
      {list && (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Scan</th>
                  <th>Scanned</th>
                  <th>Production product</th>
                  <th>Status</th>
                  <th>Latest run</th>
                  <th>Reviewer</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((c) => {
                  const sports = /cards/i.test(c.productionProduct.console);
                  return (
                    <tr
                      key={c.id}
                      className="clickable"
                      onClick={() => navigate(reviewUrl(c.id))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") navigate(reviewUrl(c.id));
                      }}
                    >
                      <td>
                        <img className="photo-thumb" src={c.photoUrl} alt="" loading="lazy" />
                      </td>
                      <td className="mono">
                        <Link to={reviewUrl(c.id)} onClick={(e) => e.stopPropagation()}>
                          {c.scanId}
                        </Link>
                      </td>
                      <td className="nowrap">{formatDate(c.scannedAt)}</td>
                      <td>
                        {c.productionProduct.id ? (
                          <>
                            <span className="muted">{c.productionProduct.console}</span> · {c.productionProduct.name}{" "}
                            <a
                              className="mono"
                              href={productUrl(c.productionProduct.id, sports ? "sports" : null)}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              #{c.productionProduct.id}
                            </a>
                          </>
                        ) : (
                          <span className="muted">uploaded photo</span>
                        )}
                      </td>
                      <td>
                        <StatusBadge status={c.status} />
                      </td>
                      <td>
                        {c.latest ? (
                          <>
                            {c.latest.pickedName ?? <span className="muted">no pick</span>}{" "}
                            <VerdictBadge verdict={c.latest.verdict} />
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="muted">{c.reviewedBy ?? ""}</td>
                    </tr>
                  );
                })}
                {list.items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="muted">
                      No cards.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Pager
            page={list.page}
            pageSize={list.pageSize}
            total={list.total}
            onPage={(p) => update({ page: String(p) })}
          />
        </>
      )}
    </>
  );
}
